import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { WorkType } from "@faire/retailer-shared/greenhouse/departmentsRequest";
import { FooterSEOContent } from "@faire/retailer-visitor-shared/components/Footer/FooterSEOContent";
import { getFilteredDepartmentByLocation, getFlattenedLocations, LimitedSectionContent, Section } from "@faire/retailer-visitor-shared/views/careers/__internal__/common/common";
import { useDepartments } from "@faire/retailer-visitor-shared/views/careers/__internal__/common/useDepartments";
import { JobOpeningsHeroSection } from "@faire/retailer-visitor-shared/views/careers/__internal__/JobOpenings/JobOpeningsHeroSection";
import { JobOpeningsList } from "@faire/retailer-visitor-shared/views/careers/__internal__/JobOpenings/JobOpeningsList";
import { LearnMoreSection } from "@faire/retailer-visitor-shared/views/careers/__internal__/JobOpenings/LearnMore";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Dropdown } from "@faire/web--source/slate/Dropdown";
import { Column, Flex, HorizontalLine } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { createContextStore } from "@faire/web--source/ui/hooks/ContextStore";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import flatten from "lodash/flatten";
import flattenDeep from "lodash/flattenDeep";
import uniq from "lodash/uniq";
import React from "react";
import styled from "styled-components";
var ALL_VALUE = "ALL";
var ALL_OPTION = function(strictLocalize) {
    return {
        label: strictLocalize({
            id: "tq+Xtr",
            defaultMessage: [
                {
                    type: 0,
                    value: "All"
                }
            ]
        }),
        value: ALL_VALUE
    };
};
var EMPLOYMENT_TITLE = function(strictLocalize) {
    return strictLocalize({
        id: "LRlgJA",
        defaultMessage: [
            {
                type: 0,
                value: "Employment"
            }
        ]
    });
};
var CONSULTANT_TITLE = function(strictLocalize) {
    return strictLocalize({
        id: "SgQGUB",
        defaultMessage: [
            {
                type: 0,
                value: "Outside Consultant"
            }
        ]
    });
};
var TITLE_WORK_TYPE_MAP = function(strictLocalize) {
    var _obj;
    return _obj = {}, _define_property(_obj, EMPLOYMENT_TITLE(strictLocalize), WorkType.EMPLOYMENT), _define_property(_obj, CONSULTANT_TITLE(strictLocalize), WorkType.CONSULTANT), _obj;
};
var WORK_TYPE_OPTIONS = function(strictLocalize) {
    return [
        {
            label: EMPLOYMENT_TITLE(strictLocalize),
            value: EMPLOYMENT_TITLE(strictLocalize)
        },
        {
            label: CONSULTANT_TITLE(strictLocalize),
            value: CONSULTANT_TITLE(strictLocalize)
        }
    ];
};
var _createContextStore = createContextStore({
    selectedDepartment: ALL_VALUE,
    selectedLocation: ALL_VALUE,
    selectedWorkType: EMPLOYMENT_TITLE
}, {
    name: "JobOpeningsStore"
}), JobOpeningsProvider = _createContextStore.Provider, useJobOpeningsStore = _createContextStore.useStore;
var JobOpeningsHeader = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useJobOpeningsStore = _sliced_to_array(useJobOpeningsStore([
        "selectedDepartment",
        "selectedLocation",
        "selectedWorkType"
    ]), 2), _useJobOpeningsStore_ = _useJobOpeningsStore[0], selectedDepartment = _useJobOpeningsStore_.selectedDepartment, selectedLocation = _useJobOpeningsStore_.selectedLocation, selectedWorkType = _useJobOpeningsStore_.selectedWorkType, setJobOpeningsStore = _useJobOpeningsStore[1];
    var _TITLE_WORK_TYPE_MAP_;
    var workType = (_TITLE_WORK_TYPE_MAP_ = TITLE_WORK_TYPE_MAP(strictLocalize)[typeof selectedWorkType === "string" ? selectedWorkType : selectedWorkType(strictLocalize)]) !== null && _TITLE_WORK_TYPE_MAP_ !== void 0 ? _TITLE_WORK_TYPE_MAP_ : WorkType.EMPLOYMENT;
    var departments = useDepartments(strictLocalize)[workType].departments;
    var departmentOptions = [
        ALL_OPTION(strictLocalize)
    ].concat(_to_consumable_array(departments.map(function(department) {
        return {
            label: department.title,
            value: department.title
        };
    })));
    var filteredDepartments = _to_consumable_array(departments);
    if (selectedDepartment !== ALL_VALUE) {
        filteredDepartments = filteredDepartments.filter(function(department) {
            return department.title === selectedDepartment;
        });
    }
    var locationOptions = [
        ALL_OPTION(strictLocalize)
    ].concat(_to_consumable_array(uniq(flattenDeep(filteredDepartments.map(function(department) {
        if (department.childDepartments.length > 0) {
            return flatten(department.childDepartments.map(function(childDepartment) {
                return childDepartment.jobs.map(function(job) {
                    return getFlattenedLocations(job.location.name);
                });
            }));
        }
        return department.jobs.map(function(job) {
            return getFlattenedLocations(job.location.name);
        });
    }))).map(function(location) {
        return {
            label: location,
            value: location
        };
    })));
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs(ResponsiveFiltersContainer, {
            children: [
                /*#__PURE__*/ _jsx(FilterDropdown, {
                    options: departmentOptions,
                    value: selectedDepartment,
                    onSelect: function(department) {
                        return setJobOpeningsStore({
                            selectedDepartment: department.value
                        });
                    },
                    label: strictLocalize({
                        id: "YL4Q3n",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Department"
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: {
                        mobileAndAbove: "1x",
                        tabletAndAbove: "2x",
                        desktopAndAbove: "3x"
                    }
                }),
                /*#__PURE__*/ _jsx(FilterDropdown, {
                    options: locationOptions,
                    value: selectedLocation,
                    onSelect: function(location) {
                        return setJobOpeningsStore({
                            selectedLocation: location.value
                        });
                    },
                    label: strictLocalize({
                        id: "jtxPy5",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Location"
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: {
                        mobileAndAbove: "1x",
                        tabletAndAbove: "2x",
                        desktopAndAbove: "3x"
                    }
                }),
                /*#__PURE__*/ _jsx(FilterDropdown, {
                    options: WORK_TYPE_OPTIONS(strictLocalize),
                    value: typeof selectedWorkType === "string" ? selectedWorkType : selectedWorkType(strictLocalize),
                    onSelect: function(workType) {
                        return setJobOpeningsStore({
                            selectedWorkType: workType.value
                        });
                    },
                    label: strictLocalize({
                        id: "pcOiw1",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Work Type"
                            }
                        ]
                    })
                })
            ]
        })
    });
};
var JobOpeningsBody = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useJobOpeningsStore = _sliced_to_array(useJobOpeningsStore([
        "selectedDepartment",
        "selectedLocation",
        "selectedWorkType"
    ]), 1), _useJobOpeningsStore_ = _useJobOpeningsStore[0], selectedDepartment = _useJobOpeningsStore_.selectedDepartment, selectedLocation = _useJobOpeningsStore_.selectedLocation, selectedWorkType = _useJobOpeningsStore_.selectedWorkType;
    var workType = (typeof selectedWorkType === "string" ? selectedWorkType : selectedWorkType(strictLocalize)) === EMPLOYMENT_TITLE(strictLocalize) ? WorkType.EMPLOYMENT : WorkType.CONSULTANT;
    var _useDepartments_workType = useDepartments(strictLocalize)[workType], isLoading = _useDepartments_workType.isLoading, isError = _useDepartments_workType.isError, departments = _useDepartments_workType.departments;
    if (isError || isLoading) {
        return /*#__PURE__*/ _jsx(ListPlaceholder, {
            children: isError ? /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "oIPtLY",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Something went wrong. Please try again later."
                        }
                    ]
                })
            }) : /*#__PURE__*/ _jsx(LoadingSpinner, {})
        });
    }
    var filteredDepartments = _to_consumable_array(departments);
    if (selectedDepartment !== ALL_VALUE) {
        filteredDepartments = filteredDepartments.filter(function(department) {
            return department.title === selectedDepartment;
        });
    }
    if (selectedLocation !== ALL_VALUE) {
        filteredDepartments = getFilteredDepartmentByLocation(filteredDepartments, selectedLocation);
    }
    return /*#__PURE__*/ _jsx(JobOpeningsList, {
        departments: filteredDepartments
    });
};
export var JobOpenings = function() {
    return /*#__PURE__*/ _jsx(JobOpeningsProvider, {
        children: /*#__PURE__*/ _jsxs(Column, {
            children: [
                /*#__PURE__*/ _jsx(JobOpeningsHeroSection, {}),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: {
                        mobileAndAbove: "5x",
                        tabletAndAbove: "10x"
                    }
                }),
                /*#__PURE__*/ _jsx(Section, {
                    $withHorizontalPadding: true,
                    align: "center",
                    children: /*#__PURE__*/ _jsxs(LimitedSectionContent, {
                        children: [
                            /*#__PURE__*/ _jsx(JobOpeningsHeader, {}),
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: "6x"
                            }),
                            /*#__PURE__*/ _jsx(HorizontalLine, {}),
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: "5x"
                            }),
                            /*#__PURE__*/ _jsx(JobOpeningsBody, {})
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: {
                        mobileAndAbove: "6x",
                        tabletAndAbove: "10x"
                    }
                }),
                /*#__PURE__*/ _jsx(LearnMoreSection, {}),
                /*#__PURE__*/ _jsx(FooterSEOContent, {})
            ]
        })
    });
};
var ListPlaceholder = styled(Column).attrs({
    justify: "center"
}).withConfig({
    componentId: "sc-ea886c67-0"
})([
    "align-self:center;min-height:240px;"
]);
var ResponsiveFiltersContainer = styled(Flex).attrs({
    direction: {
        mobileAndAbove: "column",
        tabletAndAbove: "row"
    }
}).withConfig({
    componentId: "sc-ea886c67-1"
})([
    ""
]);
var FilterDropdown = styled(Dropdown).withConfig({
    componentId: "sc-ea886c67-2"
})([
    "width:100%;",
    "{width:320px;}"
], responsiveMediaQueries.desktopAndAbove);
